// Fila del panel de notificaciones. Sirve igual para eventos del sistema y
// para mensajes de WhatsApp: lo unico que cambia es el arreglo de chips y el
// icono (modulo vs iniciales del cliente).
//
// El componente no conoce ningun campo del backend por su nombre: la
// informacion visible se define en Arrays/Notificaciones/ChipsNotificacionesArray.

import React, { useMemo } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Tooltip } from "@mui/material";

import { obtenerAreaNotificacion, AREAS_NOTIFICACION } from "../../../Arrays/Notificaciones/ModulosNotificacionesArray";
import {
    CHIPS_NOTIFICACION_SISTEMA,
    CHIPS_NOTIFICACION_WHATSAPP,
} from "../../../Arrays/Notificaciones/ChipsNotificacionesArray";
import {
    obtenerFechaNotificacion,
    formatearFechaRelativa,
    formatearFechaCompleta,
} from "../../../Helpers/Notificaciones/FechaNotificacion";
import { capitalizePalabras } from "../../../Helpers/CapitalizePalabras";

const CANAL_WHATSAPP = "whatsapp";

// Iniciales del cliente para el avatar de WhatsApp. Maximo dos letras.
const obtenerIniciales = (nombre) => {
    const palabras = String(nombre || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (palabras.length === 0) return "?";
    if (palabras.length === 1) return palabras[0].slice(0, 2).toUpperCase();
    return `${palabras[0][0]}${palabras[1][0]}`.toUpperCase();
};

export const NotificacionItem = ({
    notificacion,
    canal = "sistema",
    // En el listado combinado se marca visualmente de que canal viene la fila.
    mostrarCanal = false,
    saliendo = false,
    onAbrir,
    onEliminar,
}) => {
    const esWhatsapp = canal === CANAL_WHATSAPP;

    const area = esWhatsapp
        ? AREAS_NOTIFICACION.whatsapp
        : obtenerAreaNotificacion(notificacion.portafolio);

    const clave = esWhatsapp ? notificacion.idMensaje : notificacion.idlog;

    const { fecha, esReal } = useMemo(
        () => obtenerFechaNotificacion(notificacion, clave),
        [notificacion, clave],
    );

    const chips = (esWhatsapp ? CHIPS_NOTIFICACION_WHATSAPP : CHIPS_NOTIFICACION_SISTEMA)
        .filter((chip) => {
            try {
                return chip.visible(notificacion);
            } catch (error) {
                return false;
            }
        });

    const titulo = capitalizePalabras(
        esWhatsapp ? notificacion.cliente : notificacion.portafolio,
    );

    const Icono = area.icono;

    const abrir = () => onAbrir && onAbrir(notificacion);

    const manejarTecla = (evento) => {
        if (evento.key === "Enter" || evento.key === " ") {
            evento.preventDefault();
            abrir();
        }
    };

    const eliminar = (evento) => {
        evento.stopPropagation();
        onEliminar && onEliminar(notificacion);
    };

    return (
        <div
            className={`nt-item nt-area--${area.clave}${saliendo ? " nt-item--saliendo" : ""}`}
            role="button"
            tabIndex={0}
            onClick={abrir}
            onKeyDown={manejarTecla}
        >
            <span className="nt-item-franja" aria-hidden="true"></span>

            <span className="nt-item-icono" aria-hidden="true">
                {esWhatsapp ? (
                    <span className="nt-item-iniciales">{obtenerIniciales(notificacion.cliente)}</span>
                ) : (
                    <Icono />
                )}
                {mostrarCanal && esWhatsapp && (
                    <span className="nt-item-canal">
                        <WhatsAppIcon />
                    </span>
                )}
            </span>

            <div className="nt-item-cuerpo">
                <div className="nt-item-encabezado">
                    <span className="nt-item-titulo" title={titulo}>
                        {titulo}
                    </span>
                    <span
                        className={`nt-item-hora${esReal ? "" : " nt-item-hora--estimada"}`}
                        title={formatearFechaCompleta(fecha, esReal)}
                    >
                        {formatearFechaRelativa(fecha)}
                    </span>
                </div>

                <p className="nt-item-mensaje">{notificacion.mensaje}</p>

                {chips.length > 0 && (
                    <div className="nt-item-chips">
                        {chips.map((chip) => (
                            <span
                                key={chip.clave}
                                className={`nt-chip nt-chip--${chip.variante || "default"}`}
                                title={chip.titulo ? chip.titulo(notificacion) : undefined}
                            >
                                {chip.texto(notificacion)}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div className="nt-item-acciones">
                <Tooltip title="Eliminar">
                    <button
                        type="button"
                        className="nt-item-eliminar"
                        onClick={eliminar}
                        aria-label="Eliminar notificacion"
                    >
                        <DeleteIcon />
                    </button>
                </Tooltip>
            </div>
        </div>
    );
};
